/**
 * TakeThisPanel — 가져다쓰기(Take-this) 패널 (queue #630 · docs/biz-specs/common-showcase.md §UAT-2026-07-06-R630)
 *
 * 노드 상세 공유 본문(ModuleDetailBody)에 삽입되는 6블록 패널(#638 이전엔 5블록). 데이터는
 * 빌드타임 생성물 `data/take-this.json`(apps/web/scripts/extract-take-this.mjs) 을 코드로
 * lookup — BE 호출 없음(#630/#638 범위 0). SUPERSEDED 노드는 호출부(ModuleDetailBody)가 렌더
 * 자체를 건너뛰지만, 이 컴포넌트도 방어적으로 한 번 더 가드한다.
 *
 * queue #638 §UAT-2026-07-07-R638: ⑥ "실제 호출 최소 코드" 블록 추가 —
 * entry.usageSnippet(common-capabilities.md `사용 예제` 추출) 부재 시 fallback 안내로 대체.
 *
 * queue #639 §UAT-2026-07-07-R639: ④블록("함께 필요한 공통") 확장 — 이행 폐쇄를 artifact
 * 단위로 집약한 entry.transitive(빌드타임 프리컴퓨트)로 "딸려오는 artifact 총 N개" 요약 +
 * 전체 목록 + 중복제거 일괄 pom(gateway 제외) 을 추가하고, unresolved 심볼을 툴팁(title)
 * 에서 명시 ⚠ 경고 목록으로 승격.
 *
 * queue #653 §UAT-2026-07-08-R653 §R653-3: ⑤블록의 리메이크 프롬프트(portPrompt/bizPrompt)가
 * `{{DOMAIN_ROOT}}` 플레이스홀더를 미치환 그대로 복사하던 결함 수정 — 패널 상단 도메인루트
 * 입력(localStorage 지속) + 복사 시 `applyDomainRoot()` 치환. 미입력 상태로 복사하면 복사는
 * 허용하되 (a) 입력 필드를 오류 보더로 강조 (b) 복사되는 텍스트 상단에 치환 안내 주석 1줄을
 * 자동 삽입한다(설계 결정 — 학습 흐름을 막지 않으면서 리터럴 `{{DOMAIN_ROOT}}` 유출을 경고).
 *
 * queue #657 §UAT-2026-07-08-R657 §R657-2: ③블록("소스 경로 + 사용 예 biz")이 `usedByBiz`
 * 의 첫 1개(firstBiz)만 노출하던 것을 전체 목록으로 확장 — 여러 biz 가 같은 공통 모듈을
 * 채택할 때 소스 리메이크 참고가 완결되도록. 라벨/라우트는 기존 `GET /api/eraf/biz/catalog`
 * 를 read-only 재사용하는 `useBizCatalogMap()` 으로 해석(신규 endpoint 0). 카탈로그
 * 로딩/실패 시에는 맵이 비어 있어 raw feature 키가 non-link `<code>` 로 graceful 렌더된다
 * (로드 성공 후 자동으로 딥링크로 승격 — 별도 isLoading/isError 분기 불요).
 *
 * queue #682 §UAT-2026-07-14-R682 §R682-3: "스캐폴드에 담기" 가 `/scaffold?preselect=` 단일
 * 코드 즉시 이동 링크 → **카트 추가 버튼**으로 전환된다(여러 모듈을 누적해서 담는 여정
 * 지원). 클릭 시 이동 없이 `useScaffoldCart().add()` + 기존 `recordSignal('TAKE')`(§R678-4①
 * 유지) + 인라인 토스트("카트에 담김 — 스캐폴드로 이동") 노출. 이미 담긴 모듈이면 버튼이
 * '✓ 담김' 상태로 전환되고 옆에 "스캐폴드 보기 →" 링크가 별도로 뜬다(재클릭은 no-op).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CopyButton as SharedCopyButton } from '@ui-kit';
import type { ApiError, ModuleNode, ModuleVersions, TakeThisData, TakeThisTransitive, TakeThisYmlOptIn } from '../types/showcase';
import { RELATED_DEMO_ROUTE } from '../data/related-demo-routes';
import { useExportModuleSource } from '../hooks/useExportModuleSource';
import { useBizCatalogMap, type BizCatalogMapEntry } from '../hooks/useBizCatalogMap';
import { useScaffoldCart } from '../../_shared/hooks/useScaffoldCart';
import { showcaseApi } from '../api/showcaseApi';
import takeThisJson from '../data/take-this.json';
import moduleVersionsJson from '../data/module-versions.json';

const TAKE_THIS_DATA = takeThisJson as unknown as TakeThisData;
// queue #637: 버전 SSOT = common_module/*/pom.xml(→ BOM 집약) — 빌드타임 추출(module-versions.json)
// 소비. 하드코딩 상수 재도입 금지 — 프리빌드 누락 시 이 import 자체가 실패해 빌드가 깨진다(회귀 방지).
const MODULE_VERSIONS = moduleVersionsJson as unknown as ModuleVersions;

interface Props {
  node: ModuleNode;
  onJumpToCode?: (code: string) => void;
}

// queue #678 §R678-4: 채택 시그널 fire-and-forget — await 미대기 · 실패 swallow(적재 실패가
// 스캐폴드 담기/좌표·호출코드 복사 UX 를 절대 차단하지 않는다 · §R662 원칙 계승).
// download-stats 쿼리 무효화는 의도적으로 생략(30s 캐시 자연 반영 · 결합 최소화, §R678-4).
function recordSignal(eventType: 'TAKE' | 'COPY', moduleCodes: string[]): void {
  void showcaseApi.recordSignal(eventType, moduleCodes).catch(() => {});
}

// ─── shared block styles ──────────────────────────────────────────────────────

const blockStyle: React.CSSProperties = {
  marginBottom: 'var(--spacing-4)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
};

const blockHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-2)',
};

const blockTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: 0,
};

const preStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  background: 'var(--color-surface-muted)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--spacing-3)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.6,
  margin: 0,
};

const noteStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--spacing-2)',
  marginBottom: 0,
};

// ─── copy button (queue #644 §R644-4: clipboard 로직은 ui-kit CopyButton 으로 승격) ──────
// TakeThisPanel 의 7개 호출부는 그대로 `{ text, ariaLabel }` 시그니처만 넘기던 관례를 유지한다
// (behavioral 무변경) — 실제 clipboard 구현은 공유 컴포넌트(SharedCopyButton)에 위임하고, 이
// 얇은 래퍼는 showcase.takeThis.copy/copied i18n 라벨만 바인딩한다(중복 clipboard 재구현 금지).

function CopyButton({ text, ariaLabel, onCopy }: { text: string; ariaLabel: string; onCopy?: () => void }) {
  const { t } = useTranslation();
  return (
    <SharedCopyButton
      text={text}
      ariaLabel={ariaLabel}
      copyLabel={t('showcase.takeThis.copy')}
      copiedLabel={t('showcase.takeThis.copied')}
      onCopy={onCopy}
    />
  );
}

function DepBadge({ code, onJumpToCode }: { code: string; onJumpToCode?: (code: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onJumpToCode?.(code)}
      style={{
        fontFamily: 'var(--font-family-mono)',
        fontSize: 'var(--font-size-xs)',
        background: 'var(--color-surface-background)',
        border: '1px solid var(--color-brand-primary)',
        color: 'var(--color-brand-primary)',
        borderRadius: 'var(--radius-sm)',
        padding: '2px var(--spacing-2)',
        cursor: onJumpToCode ? 'pointer' : 'default',
      }}
    >
      {code}
    </button>
  );
}

// ─── usedByBiz link item (queue #657 · GET /biz/catalog 로 해석한 label/url) ──────────

function BizUsedByLink({ feature, bizMap }: { feature: string; bizMap: Map<string, BizCatalogMapEntry> }) {
  const { t } = useTranslation();
  const entry = bizMap.get(feature);
  const label = entry?.nameKo ?? feature;
  if (!entry?.url) {
    return <code>{label}</code>;
  }
  return (
    <Link
      to={entry.url}
      aria-label={t('showcase.takeThis.source.bizLinkAria', { biz: label })}
      style={{ color: 'var(--color-brand-primary)' }}
    >
      {label}
    </Link>
  );
}

// ─── source export button (queue #631 · GET /catalog/modules/{code}/export) ──

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ExportSourceButton({ code }: { code: string }) {
  const { t } = useTranslation();
  const { mutate, isPending, error, isError, reset } = useExportModuleSource();
  const [toastVisible, setToastVisible] = useState(false);

  function handleClick() {
    setToastVisible(false);
    reset();
    mutate(code, {
      onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
      onError: () => setToastVisible(true),
    });
  }

  const apiError = error as ApiError | null;

  return (
    <div style={{ marginTop: 'var(--spacing-3)' }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isPending}
        aria-busy={isPending}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-4)',
          background: isPending ? 'var(--color-surface-elevated)' : 'var(--color-brand-primary)',
          color: isPending ? 'var(--color-text-secondary)' : 'var(--color-text-inverse)',
          border: isPending ? '1px solid var(--color-surface-border)' : 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          cursor: isPending ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        {isPending && (
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 12,
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: 'var(--color-text-inverse)',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        )}
        {isPending ? t('showcase.takeThis.source.exportLoading') : t('showcase.takeThis.source.exportButton')}
      </button>

      {isError && toastVisible && apiError && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            marginTop: 'var(--spacing-2)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3)',
            border: '1px solid var(--color-danger, #dc2626)',
            borderLeft: '4px solid var(--color-danger, #dc2626)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-muted)',
            maxWidth: 420,
          }}
        >
          <div style={{ flex: 1 }}>
            <strong style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
              {apiError.code}
            </strong>
            <p style={{ margin: 'var(--spacing-1) 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}>
              {apiError.message}
            </p>
          </div>
          <button
            type="button"
            aria-label={t('showcase.takeThis.source.exportToastClose')}
            onClick={() => setToastVisible(false)}
            style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 0 }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ─── prompt templates (consumer-guide.md §4.3 ①/② 정합 — 고정 한국어 산출물) ────

function buildPortModulePrompt(node: ModuleNode, moduleDir: string, specPath: string): string {
  const apis = node.publicApis.slice(0, 3).join('/') || node.artifact;
  return `common_module/${moduleDir} 의 ${node.nameKo}(${node.code}) 능력을 우리 프로젝트 공통 모듈로\n이식해줘. ${specPath} 스펙을 근거로 하고, 패키지는 {{DOMAIN_ROOT}}.common 으로.\n${apis} 구조를 유지하되 우리 CLAUDE.md 규약(공통 재구현 금지)에 맞춰줘.`;
}

function buildBizRemakePrompt(node: ModuleNode, biz: string): string {
  const apis = node.publicApis.slice(0, 2).join('·') || node.artifact;
  return `eraf-src 의 ${biz} biz(예제)를 참고해서 우리 <자사 화면명> 화면을 리메이크해줘.\ndocs/biz-specs/${biz}.md 스펙과 docs/biz-designs/${biz}.html 설계를 근거로,\n공통 모듈(${apis})은 재구현하지 말고 import 해서 사용.\nURL prefix 는 {{URL_PREFIX}}, 패키지는 {{DOMAIN_ROOT}}.biz.${biz.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase())}.`;
}

// ─── DOMAIN_ROOT 치환 (queue #653 §R653-3) ──────────────────────────────────────

const DOMAIN_ROOT_STORAGE_KEY = 'eraf.takeThis.domainRoot';
const DOMAIN_ROOT_PLACEHOLDER = '{{DOMAIN_ROOT}}';

function readStoredDomainRoot(): string {
  try {
    return window.localStorage.getItem(DOMAIN_ROOT_STORAGE_KEY) ?? '';
  } catch {
    // localStorage 미가용(프라이빗 모드 등) — 세션 지속 없이 빈 값으로 시작.
    return '';
  }
}

function writeStoredDomainRoot(value: string): void {
  try {
    window.localStorage.setItem(DOMAIN_ROOT_STORAGE_KEY, value);
  } catch {
    // 무음 실패 — 지속 실패는 치환 기능 자체를 막지 않는다.
  }
}

/**
 * §R653-3 채택안: 복사는 항상 허용하되, domainRoot 미입력 시 리터럴 플레이스홀더를 그대로
 * 내보내는 대신 상단에 치환 안내 주석 1줄을 자동 삽입한다. `{{DOMAIN_ROOT}}` 를 포함하지
 * 않는 텍스트는 그대로 통과(no-op) — usageSnippet 등 다른 복사 경로에 재사용 가능한 단일
 * 유틸(호출부가 없으면 굳이 적용하지 않아도 부작용 없음).
 */
function applyDomainRoot(text: string, domainRoot: string, warningComment: string): string {
  if (!text.includes(DOMAIN_ROOT_PLACEHOLDER)) return text;
  const trimmed = domainRoot.trim();
  if (!trimmed) {
    return `# ${warningComment}\n${text}`;
  }
  return text.split(DOMAIN_ROOT_PLACEHOLDER).join(trimmed);
}

// ─── scaffold cart control (queue #682 §R682-3) ────────────────────────────────
// 담기 = 카트 추가(이동 없음) + 토스트. 이미 담긴 상태면 '✓ 담김' + "스캐폴드 보기 →" 링크.
// 토스트는 §R631 ExportSourceButton 인라인 토스트 스타일을 답습(신규 전역 토스트 시스템 도입 안 함).

function ScaffoldCartControl({ node }: { node: ModuleNode }) {
  const { t } = useTranslation();
  const cart = useScaffoldCart();
  const [toastVisible, setToastVisible] = useState(false);
  const added = cart.has(node.code);

  useEffect(() => {
    if (!toastVisible) return;
    const timer = setTimeout(() => setToastVisible(false), 4000);
    return () => clearTimeout(timer);
  }, [toastVisible]);

  function handleClick() {
    if (added) return; // 멱등 — 재추가 no-op
    cart.add(node.code);
    // queue #678 §R678-4①: 클릭 시 TAKE 시그널(fire-and-forget) — 이동은 이제 없지만 발화 시점은 동일(담기 클릭).
    recordSignal('TAKE', [node.code]);
    setToastVisible(true);
  }

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={added}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-1)',
          padding: 'var(--spacing-1) var(--spacing-3)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          border: '1px solid var(--color-brand-primary)',
          borderRadius: 'var(--radius-full)',
          color: added ? 'var(--color-text-inverse)' : 'var(--color-brand-primary)',
          background: added ? 'var(--color-brand-primary)' : 'transparent',
          cursor: 'pointer',
        }}
      >
        {added ? t('showcase.cart.added') : `📦 ${t('projectScaffold.takeThis.addToScaffold')}`}
      </button>
      {added && (
        <Link to="/scaffold" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)' }}>
          {t('showcase.cart.viewScaffold')} →
        </Link>
      )}
      {toastVisible && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 'var(--spacing-2)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-brand-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-primary)',
            boxShadow: 'var(--shadow-md)',
            whiteSpace: 'nowrap',
          }}
        >
          <span>✓ {t('showcase.cart.toast.added')}</span>
          <Link to="/scaffold" style={{ color: 'var(--color-brand-primary)', fontWeight: 600 }}>
            {t('showcase.cart.toast.goto')}
          </Link>
          <button
            type="button"
            aria-label={t('showcase.cart.toast.close')}
            onClick={() => setToastVisible(false)}
            style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 0 }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ─── main panel ───────────────────────────────────────────────────────────────

export function TakeThisPanel({ node, onJumpToCode }: Props) {
  const { t } = useTranslation();
  // §R653-3: DOMAIN_ROOT 입력 — localStorage 지연 초기화(useState 콜백)로 첫 렌더부터 복원.
  const [domainRoot, setDomainRoot] = useState<string>(readStoredDomainRoot);
  const [domainRootWarn, setDomainRootWarn] = useState(false);
  // §R657-2: usedByBiz 라벨/딥링크 해석 — 로딩/실패 시 bizMap 이 비어 있어 raw 키가
  // non-link 로 graceful 렌더되고, 로드 성공 후 자동으로 딥링크로 승격된다.
  const { bizMap } = useBizCatalogMap();

  function handleDomainRootChange(value: string) {
    setDomainRoot(value);
    writeStoredDomainRoot(value);
    if (value.trim()) setDomainRootWarn(false);
  }

  // 미입력 상태로 ⑤블록 프롬프트를 복사하면 필드를 강조 — 안내 주석 삽입 자체는
  // applyDomainRoot() 가 텍스트 레벨에서 항상 처리(클릭 여부 무관).
  function handlePromptCopyAttempt() {
    if (!domainRoot.trim()) setDomainRootWarn(true);
  }

  // §R630-0: SUPERSEDED 노드는 미표시 (SupersedeNotice 가 우선) — 방어적 가드.
  if (node.supersededBy != null) return null;

  const entry = TAKE_THIS_DATA[node.code];
  if (!entry) return null;

  const moduleDir = entry.sourcePath.replace(/^common_module\//, '');
  // §R637-2: 모듈 dependency 는 <version> 을 생략 유지(BOM 관리)하되, 소비자 투명성을 위해
  // 실버전을 주석으로 부기 — moduleVersions.modules[node.artifact] 없으면(예: eraf-gateway
  // 는 BOM 집약 있음, 미탑재 opt-in 신규 모듈 등) 부기 생략.
  const declaredVersion = MODULE_VERSIONS.modules[node.artifact];
  const moduleComment = declaredVersion
    ? `<!-- 이 능력 모듈: ${node.artifact}:${declaredVersion} (버전은 BOM 이 관리 · 좌표에선 생략) -->`
    : '<!-- 이 능력 모듈 (버전 생략 — BOM 이 관리) -->';
  const mavenSnippet = `<!-- eraf-bom import (dependencyManagement · 버전 일괄 관리) -->\n<dependency>\n  <groupId>com.skax.eraf</groupId>\n  <artifactId>eraf-bom</artifactId>\n  <version>${MODULE_VERSIONS.bomVersion}</version>\n  <type>pom</type>\n  <scope>import</scope>\n</dependency>\n\n${moduleComment}\n<dependency>\n  <groupId>com.skax.eraf</groupId>\n  <artifactId>${node.artifact}</artifactId>\n</dependency>`;

  const related = RELATED_DEMO_ROUTE[node.code];
  const firstBiz = node.usedByBiz?.[0];
  // §R657-2: ③블록 "사용 예 biz" 는 첫 1개(firstBiz)가 아니라 usedByBiz 전체를 렌더한다.
  // firstBiz 는 ⑤블록(리메이크 프롬프트 예시)에서 그대로 유지 사용.
  const bizList = node.usedByBiz ?? [];

  const portPrompt = buildPortModulePrompt(node, moduleDir, entry.specPath);
  const bizPrompt = firstBiz ? buildBizRemakePrompt(node, firstBiz) : null;

  // §R639-4: artifactCount===0 이면 일괄 pom 은 블록①(단일 모듈)과 동일하므로 미표시.
  const hasGatewayInTransitive = entry.transitive.artifacts.some((a) => a.isGateway);
  const transitivePomSnippet = entry.transitive.artifactCount > 0
    ? buildTransitivePomSnippet(node, entry.transitive, MODULE_VERSIONS.bomVersion)
    : '';

  return (
    <section aria-label={t('showcase.takeThis.title')} style={{ marginTop: 'var(--spacing-6)', marginBottom: 'var(--spacing-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-3)', flexWrap: 'wrap', marginBottom: 'var(--spacing-3)' }}>
        <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
          {t('showcase.takeThis.title')}
        </h3>
        {/* queue #682 §R682-3: 스캐폴드 담기 카트 — 클릭 시 이동 없이 카트 추가 + 토스트 */}
        <ScaffoldCartControl node={node} />
      </div>

      {/* queue #653 §R653-3: DOMAIN_ROOT 입력 — ⑤블록 프롬프트의 {{DOMAIN_ROOT}} 치환 원천.
          미입력 상태로 ⑤블록을 복사하면 domainRootWarn=true 로 전환되어 아래 필드가 강조된다. */}
      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <label
          htmlFor="take-this-domain-root"
          style={{
            display: 'block',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-1)',
          }}
        >
          {t('showcase.takeThis.domainRoot.label')}
        </label>
        <input
          id="take-this-domain-root"
          type="text"
          value={domainRoot}
          onChange={(e) => handleDomainRootChange(e.target.value)}
          placeholder={t('showcase.takeThis.domainRoot.placeholder')}
          aria-invalid={domainRootWarn}
          aria-describedby={domainRootWarn ? 'take-this-domain-root-warning' : undefined}
          style={{
            width: '100%',
            maxWidth: 360,
            padding: 'var(--spacing-2) var(--spacing-3)',
            border: `1px solid ${domainRootWarn ? 'var(--color-danger, #dc2626)' : 'var(--color-surface-border)'}`,
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family-mono)',
            background: 'var(--color-surface-background)',
            color: 'var(--color-text-primary)',
            boxSizing: 'border-box',
          }}
        />
        {domainRootWarn && (
          <p
            id="take-this-domain-root-warning"
            role="alert"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)', marginTop: 'var(--spacing-1)' }}
          >
            {t('showcase.takeThis.domainRoot.warning', { DOMAIN_ROOT: DOMAIN_ROOT_PLACEHOLDER })}
          </p>
        )}
      </div>

      {/* ① maven 좌표 */}
      <div style={blockStyle}>
        <div style={blockHeaderStyle}>
          <p style={blockTitleStyle}>{t('showcase.takeThis.maven.title')}</p>
          {/* queue #678 §R678-4②: 좌표 복사 → COPY 시그널(fire-and-forget) */}
          <CopyButton
            text={mavenSnippet}
            ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.takeThis.maven.title') })}
            onCopy={() => recordSignal('COPY', [node.code])}
          />
        </div>
        <pre style={preStyle}>{mavenSnippet}</pre>
        <p style={noteStyle}>{t('showcase.takeThis.maven.nexusNote')}</p>
        {entry.isGatewayArtifact && <p style={noteStyle}>{t('showcase.takeThis.maven.gatewayNote')}</p>}
      </div>

      {/* ② 최소 활성 yml */}
      <div style={blockStyle}>
        <div style={blockHeaderStyle}>
          <p style={blockTitleStyle}>{t('showcase.takeThis.yml.title')}</p>
          {!entry.yml.alwaysOn && (
            <CopyButton
              text={buildYmlSnippet(entry.yml, t('showcase.takeThis.yml.relatedConfigNote'))}
              ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.takeThis.yml.title') })}
            />
          )}
        </div>
        {entry.yml.alwaysOn ? (
          <span
            style={{
              display: 'inline-block',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              background: 'var(--color-surface-muted)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px var(--spacing-2)',
            }}
          >
            {t('showcase.takeThis.yml.alwaysOnBadge')}
          </span>
        ) : (
          <>
            {entry.yml.hardInject && (
              <span
                role="alert"
                style={{
                  display: 'inline-block',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--color-text-inverse)',
                  background: 'var(--color-danger, #dc2626)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px var(--spacing-2)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                {t('showcase.takeThis.yml.hardInjectWarning')}
              </span>
            )}
            <pre style={preStyle}>{buildYmlSnippet(entry.yml, t('showcase.takeThis.yml.relatedConfigNote'))}</pre>
            {entry.yml.andGates && entry.yml.andGates.length > 0 && (
              <p style={noteStyle}>{t('showcase.takeThis.yml.andGateNote', { gate: entry.yml.andGates.join(', ') })}</p>
            )}
            {entry.yml.note && <p style={noteStyle}>{t('showcase.takeThis.yml.note')}: {entry.yml.note}</p>}
          </>
        )}
      </div>

      {/* ③ 소스 경로 + 사용 예 biz */}
      <div style={blockStyle}>
        <div style={blockHeaderStyle}>
          <p style={blockTitleStyle}>{t('showcase.takeThis.source.title')}</p>
          <CopyButton text={entry.sourcePath} ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.takeThis.source.title') })} />
        </div>
        <code style={{ ...preStyle, display: 'block' }}>{entry.sourcePath}</code>
        <p style={noteStyle}>{t('showcase.takeThis.source.specLabel')}: <code>{entry.specPath}</code></p>
        {/* §R657-2: usedByBiz 전체 목록 — 첫 6개 인라인 + >6 이면 <details> 접기.
            0건이면 기존 폴백대로 이 문단 자체를 렌더하지 않는다(related 데모링크 포함). */}
        {bizList.length > 0 && (
          <div style={noteStyle}>
            {t('showcase.takeThis.source.usedByLabel')}:{' '}
            {bizList.slice(0, 6).map((feature, idx) => (
              <span key={feature}>
                {idx > 0 && ', '}
                <BizUsedByLink feature={feature} bizMap={bizMap} />
              </span>
            ))}
            {bizList.length > 6 && (
              <details style={{ display: 'inline' }}>
                <summary style={{ display: 'inline', cursor: 'pointer', color: 'var(--color-brand-primary)' }}>
                  {t('showcase.takeThis.source.usedByMore', { count: bizList.length - 6 })}
                </summary>
                {bizList.slice(6).map((feature, idx) => (
                  <span key={feature}>
                    {idx > 0 && ', '}
                    <BizUsedByLink feature={feature} bizMap={bizMap} />
                  </span>
                ))}
              </details>
            )}
            {related && (
              <>
                {' · '}
                <Link to={related.route} style={{ color: 'var(--color-brand-primary)' }}>
                  {t('showcase.takeThis.source.demoLink')}
                </Link>
              </>
            )}
          </div>
        )}
        <ExportSourceButton code={node.code} />
      </div>

      {/* ④ depends_on 이행 폐쇄 배지 + artifact 집약 요약(#639) */}
      <div style={blockStyle}>
        <p style={blockTitleStyle}>{t('showcase.takeThis.deps.title')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)' }}>
          {entry.deps.codes.length > 0 ? (
            entry.deps.codes.map((c) => <DepBadge key={c} code={c} onJumpToCode={onJumpToCode} />)
          ) : (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              {t('showcase.takeThis.deps.empty')}
            </span>
          )}
        </div>

        {/* §R639-4: artifact 단위 요약 + 전체 목록 (코드 수 ≠ artifact 수) */}
        <p style={noteStyle}>
          {entry.transitive.artifactCount > 0
            ? t('showcase.takeThis.transitive.summary', { count: entry.transitive.artifactCount })
            : t('showcase.takeThis.transitive.selfContained')}
        </p>

        {entry.transitive.artifactCount > 0 && (
          <details style={{ marginTop: 'var(--spacing-2)' }}>
            <summary style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)', cursor: 'pointer' }}>
              {t('showcase.takeThis.transitive.listToggle')}
            </summary>
            <ul style={{ margin: 'var(--spacing-2) 0 0', paddingLeft: 'var(--spacing-4)' }}>
              {entry.transitive.artifacts.map((a) => (
                <li key={a.artifact} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                  {t('showcase.takeThis.transitive.artifactCodes', { artifact: a.artifact, codes: a.codes.join(', ') })}
                  {a.isGateway && (
                    <span role="alert" style={{ marginLeft: 'var(--spacing-2)', color: 'var(--color-danger, #dc2626)', fontWeight: 600 }}>
                      ⚠ {t('showcase.takeThis.transitive.gatewayItemNote')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* §R639-4: 중복제거 일괄 dependency pom — self + 비-gateway 전이 artifact (artifactCount===0 이면 ①과 중복이라 미표시) */}
        {entry.transitive.artifactCount > 0 && (
          <div style={{ marginTop: 'var(--spacing-3)' }}>
            <div style={blockHeaderStyle}>
              <p style={blockTitleStyle}>{t('showcase.takeThis.transitive.pomTitle')}</p>
              <CopyButton
                text={transitivePomSnippet}
                ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.takeThis.transitive.pomTitle') })}
              />
            </div>
            <pre style={preStyle}>{transitivePomSnippet}</pre>
            <p style={noteStyle}>{t('showcase.takeThis.transitive.pomNote')}</p>
            {hasGatewayInTransitive && (
              <p style={noteStyle}>{t('showcase.takeThis.transitive.gatewayExcludedNote')}</p>
            )}
          </div>
        )}

        {/* §R639-4: unresolved 승격 — 툴팁(title) 폐지, 목록 내 명시 ⚠ 경고 행(role="alert") */}
        {entry.deps.unresolved.length > 0 && (
          <div style={{ marginTop: 'var(--spacing-3)' }}>
            <p style={{ ...blockTitleStyle, fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
              {t('showcase.takeThis.transitive.unresolvedTitle')}
            </p>
            <ul style={{ margin: 'var(--spacing-1) 0 0', paddingLeft: 'var(--spacing-4)' }}>
              {entry.deps.unresolved.map((symbol) => (
                <li key={symbol} role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
                  ⚠ {t('showcase.takeThis.transitive.unresolvedItem', { symbol })}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ⑤ Claude Code 리메이크 프롬프트 (queue #653: 복사 시 {{DOMAIN_ROOT}} 치환) */}
      <div style={blockStyle}>
        <div style={blockHeaderStyle}>
          <p style={blockTitleStyle}>{t('showcase.takeThis.prompt.title')}</p>
          <CopyButton
            text={applyDomainRoot(portPrompt, domainRoot, t('showcase.takeThis.domainRoot.copyWarningComment', { DOMAIN_ROOT: DOMAIN_ROOT_PLACEHOLDER }))}
            ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.takeThis.prompt.title') })}
            onCopy={handlePromptCopyAttempt}
          />
        </div>
        <pre style={preStyle}>{portPrompt}</pre>
        {bizPrompt && (
          <>
            <div style={{ ...blockHeaderStyle, marginTop: 'var(--spacing-3)' }}>
              <p style={noteStyle}>{firstBiz}</p>
              <CopyButton
                text={applyDomainRoot(bizPrompt, domainRoot, t('showcase.takeThis.domainRoot.copyWarningComment', { DOMAIN_ROOT: DOMAIN_ROOT_PLACEHOLDER }))}
                ariaLabel={t('showcase.takeThis.copyAria', { block: `${t('showcase.takeThis.prompt.title')} (${firstBiz})` })}
                onCopy={handlePromptCopyAttempt}
              />
            </div>
            <pre style={preStyle}>{bizPrompt}</pre>
          </>
        )}
      </div>

      {/* ⑥ 실제 호출 최소 코드 (queue #638 · common-capabilities.md `사용 예제` 추출) */}
      <div style={blockStyle}>
        <div style={blockHeaderStyle}>
          <p style={blockTitleStyle}>{t('showcase.takeThis.usage.title')}</p>
          {entry.usageSnippet && (
            // queue #678 §R678-4②: 호출코드 복사 → COPY 시그널(fire-and-forget)
            <CopyButton
              text={entry.usageSnippet}
              ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.takeThis.usage.title') })}
              onCopy={() => recordSignal('COPY', [node.code])}
            />
          )}
        </div>
        {entry.usageSnippet ? (
          <>
            <pre style={preStyle}>{entry.usageSnippet}</pre>
            <p style={noteStyle}>{t('showcase.takeThis.usage.sourceNote')}</p>
          </>
        ) : (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic', margin: 0 }}>
            {t('showcase.takeThis.usage.empty')}
          </p>
        )}
      </div>
    </section>
  );
}

// §R639-4: self artifact + 비-gateway 전이 artifact 를 dedup·정렬해 일괄 dependency pom 조립.
// eraf-bom import 블록은 ①(단일 모듈) 스니펫과 동형(버전은 BOM 관리 · 개별 <version> 생략).
function buildTransitivePomSnippet(node: ModuleNode, transitive: TakeThisTransitive, bomVersion: string): string {
  const artifacts = [node.artifact, ...transitive.artifacts.filter((a) => !a.isGateway).map((a) => a.artifact)];
  const uniqueSorted = [...new Set(artifacts)].sort();
  const bomBlock = `<!-- eraf-bom import (dependencyManagement · 버전 일괄 관리) -->\n<dependency>\n  <groupId>com.skax.eraf</groupId>\n  <artifactId>eraf-bom</artifactId>\n  <version>${bomVersion}</version>\n  <type>pom</type>\n  <scope>import</scope>\n</dependency>`;
  const depsBlock = uniqueSorted
    .map((artifact) => `<dependency>\n  <groupId>com.skax.eraf</groupId>\n  <artifactId>${artifact}</artifactId>\n</dependency>`)
    .join('\n\n');
  return `${bomBlock}\n\n${depsBlock}`;
}

function buildYmlSnippet(yml: TakeThisYmlOptIn, relatedConfigNote?: string): string {
  const gateLines = [yml.gateKey, ...(yml.andGates ?? [])].map((g) => `${g}: true`).join('\n');
  const relatedConfig = yml.relatedConfig ?? [];
  if (relatedConfig.length === 0) return gateLines;
  // §R638-4: relatedConfig 는 실값 placeholder 예시 — 오해 방지 헤더 1줄을 주석으로 선행.
  const relatedLines = [
    relatedConfigNote ? `# ${relatedConfigNote}` : null,
    ...relatedConfig.map((c) => `# ${c}`),
  ].filter((line): line is string => line !== null).join('\n');
  return `${gateLines}\n${relatedLines}`;
}
